import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { PlayCircle, ArrowRight, Sparkles, Film, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const glass = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";

/**
 * Shortest possible top-of-funnel share flow. A single sizzle video plays, and
 * as soon as it ends (or the visitor clicks Continue) they land in the
 * sponsor's regular funnel at /join?ref={slug} — no form, no long
 * presentation. Distinct from /discover which is the full sizzle → contact →
 * presentation multi-step lead-capture path.
 */
export default function SizzleToFunnel() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [ended, setEnded] = useState(false);
  const maxRef = useRef(0);
  const lastSent = useRef(0);

  const inviteToken = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const i = p.get("i") || localStorage.getItem("discover_invite") || "";
    if (i) localStorage.setItem("discover_invite", i);
    return i;
  }, []);
  const sid = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const inv = p.get("i") || localStorage.getItem("discover_invite") || "";
    if (inv) return `inv-${inv}`.slice(0, 80);
    let s = localStorage.getItem("discover_sid");
    if (!s) { s = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`); localStorage.setItem("discover_sid", s); }
    return s;
  }, []);
  const refSlug = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const r = p.get("ref") || p.get("inviteCode") || localStorage.getItem("discover_ref") || "";
    if (r) localStorage.setItem("discover_ref", r);
    return r;
  }, []);
  const refSuffix = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const s2 = p.get("s") || p.get("suffix") || localStorage.getItem("discover_suffix") || "";
    if (s2) localStorage.setItem("discover_suffix", s2);
    return s2;
  }, []);
  // Marketing campaign key (?k=) — the wallet behind it is resolved server-side.
  const utmKey = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const k = p.get("k") || localStorage.getItem("discover_utm") || "";
    if (k) localStorage.setItem("discover_utm", k);
    return k;
  }, []);

  useEffect(() => {
    axios.get(`${API}/discover/content`, { params: refSlug ? { ref: refSlug } : {} })
      .then((r) => setContent(r.data)).catch(() => setContent({}));
  }, [refSlug]);

  // Register an emailed-invite click the moment they land — shows in Prospects
  // (identified by the email we sent to) even if they watch nothing.
  useEffect(() => {
    if (!inviteToken) return;
    axios.post(`${API}/discover/land`, { session_id: sid, ref: refSlug || null, suffix: refSuffix || null, invite_token: inviteToken, utm_key: utmKey || null }).catch(() => {});
  }, [inviteToken, sid, refSlug, refSuffix, utmKey]);

  const track = (pct, isEnded = false) => {
    axios.post(`${API}/discover/track`, {
      session_id: sid, video: "sizzle", pct: Math.round(pct * 10) / 10,
      ended: isEnded, ref: refSlug || null, suffix: refSuffix || null, invite_token: inviteToken || null,
      utm_key: utmKey || null,
    }).catch(() => {});
  };

  const onTime = (e) => {
    const v = e.currentTarget;
    if (!v.duration) return;
    const pct = (v.currentTime / v.duration) * 100;
    if (pct > maxRef.current) maxRef.current = pct;
    const now = Date.now();
    if (now - lastSent.current > 8000) { lastSent.current = now; track(maxRef.current); }
  };

  const continueToFunnel = () => {
    const q = new URLSearchParams();
    if (refSlug) q.set("ref", refSlug);
    if (refSuffix) q.set("s", refSuffix);
    if (utmKey) q.set("k", utmKey);
    navigate(`/join?${q.toString()}`);
  };

  return (
    <div className="min-h-screen text-slate-100" style={{ background: "radial-gradient(1200px 600px at 70% -10%, rgba(64,96,255,0.18), transparent), radial-gradient(900px 500px at 0% 100%, rgba(124,58,237,0.14), transparent), #070B14" }} data-testid="sizzle-to-funnel-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#4060FF,#7C3AED)" }}><Sparkles size={17} /></div>
            <div>
              <div className="font-display font-bold tracking-tight">BotChainCP</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5" data-testid="s2f-progress">
            <span className="h-1.5 rounded-full transition-all" style={{ width: ended ? 12 : 26, background: "#4060FF" }} title="Watch" />
            <span className="h-1.5 rounded-full transition-all" style={{ width: ended ? 26 : 12, background: ended ? "#4060FF" : "rgba(255,255,255,0.14)" }} title="Start onboarding" />
          </div>
        </div>

        {!content ? (
          <div className="text-center py-24"><Loader2 className="animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className={`${glass} p-6`} data-testid="s2f-card">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2"><Film size={12} /> Watch this first · ~2 minutes</div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-1">See what this is about</h1>
            <p className="text-sm text-slate-400 mb-4">
              When the video ends, you&apos;ll be able to start the setup.
            </p>

            {content.sizzle ? (
              <video
                src={`${process.env.REACT_APP_BACKEND_URL}${content.sizzle.url}`}
                controls playsInline preload="metadata" controlsList="nodownload"
                className="w-full rounded-xl bg-black aspect-video"
                onTimeUpdate={onTime}
                onPause={() => track(maxRef.current)}
                onEnded={() => { maxRef.current = 100; track(100, true); setEnded(true); }}
                data-testid="s2f-video"
              />
            ) : (
              <div className="rounded-xl border border-white/10 bg-black/40 aspect-video flex flex-col items-center justify-center text-slate-500 gap-2" data-testid="s2f-video-missing">
                <PlayCircle size={36} /> <span className="text-sm">Video is being prepared — check back shortly.</span>
              </div>
            )}

            <div className={`mt-5 rounded-xl border transition-all ${ended ? "border-emerald-400/40 bg-emerald-400/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
              <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  {ended ? (
                    <div className="flex items-start gap-2">
                      <CheckCircle2 size={18} className="text-emerald-400 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-semibold text-white">You&apos;ve seen it — ready to get started?</div>
                        <div className="text-xs text-slate-400 mt-0.5">Onboarding is guided step-by-step. Takes about 10 minutes.</div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <PlayCircle size={18} className="text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-semibold text-white">Not ready yet?</div>
                        <div className="text-xs text-slate-400 mt-0.5">You can skip the video and go straight into setup if you&apos;d like.</div>
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  onClick={continueToFunnel}
                  className="h-11 px-6 font-semibold shrink-0"
                  style={{ background: ended ? "linear-gradient(135deg,#059669,#10B981)" : "linear-gradient(135deg,#4060FF,#7C3AED)" }}
                  data-testid="s2f-continue"
                >
                  {ended ? "Start onboarding" : "Skip to onboarding"} <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="text-center text-[11px] text-slate-600 mt-8">BotChainCP · CaryPact operations</div>
      </div>
    </div>
  );
}
